import { cacheClubShields } from '../state/club-shield-cache';
import { clubPath } from '../state/club-session';
/**
 * Competition & Match Engine API Service (Microservicio de Competición y Calendario)
 * Conecta con los endpoints del backend y en caso de que aún no existan o fallen,
 * simula la llamada cargando el mock (.json) desde /mocks/competition/
 */
import {
  PlayerEditorialRating,
  UserCompetitionsState,
  CompetitionStructure,
  GroupStandingItem,
  CompetitionMatchdayResponse,
  ActiveCompetitionContext,
  MatchLiveResponse,
  NextMatchResponse,
  TacticalLineupResponse,
  ConfirmSubstitutionWindowPayload,
  SubstitutionWindowResponse,
  SubstitutionWindow,
} from '../models/types';
import { apiSimulator } from './api-simulator.service';

/**
 * Extrae de forma canónica el contexto activo de competición y club desde la respuesta
 * de GET /api/me/competitions.
 */
export function extractActiveCompetitionContext(state: any): ActiveCompetitionContext {
  if (!state) {
    return { status: 'NOT_JOINED' };
  }

  const parts: any[] = Array.isArray(state)
    ? state
    : state.participations || state.global?.participations || [];

  const activePart =
    parts.find(
      (p: any) =>
        p?.status?.toUpperCase() === 'ACTIVE' ||
        p?.active === true ||
        p?.isCurrent === true
    ) || (parts.length > 0 ? parts[0] : null);

  const rawClubId =
    activePart?.clubId ||
    (typeof activePart?.club === 'object' ? activePart?.club?.id : null) ||
    state.clubId ||
    (typeof state.club === 'object' ? state.club?.id : null) ||
    state.global?.clubId ||
    (typeof state.global?.club === 'object' ? state.global?.club?.id : null) ||
    null;

  const rawSeasonId =
    activePart?.seasonId ||
    state.seasonId ||
    state.global?.seasonId ||
    null;

  const rawUnitId =
    activePart?.competitionUnitId ||
    activePart?.unitId ||
    state.competitionUnitId ||
    state.unitId ||
    state.global?.competitionUnitId ||
    state.global?.unitId ||
    (activePart?.groupId && activePart.groupId !== 'league-global-01' ? activePart.groupId : null) ||
    (state.global?.groupId && state.global.groupId !== 'league-global-01' ? state.global.groupId : null) ||
    null;

  const rawCompetitionId =
    activePart?.competitionId ||
    state.competitionId ||
    state.global?.competitionId ||
    'comp-global-01';

  const clubName =
    activePart?.clubName ||
    (typeof activePart?.club === 'object' ? activePart?.club?.name : null) ||
    (typeof state.club === 'object' ? state.club?.name : null) ||
    state.global?.club?.name ||
    '';

  const clubCode =
    activePart?.clubCode ||
    (typeof activePart?.club === 'object' ? activePart?.club?.code : null) ||
    (typeof state.club === 'object' ? state.club?.code : null) ||
    state.global?.club?.code ||
    '';

  let divisionName =
    activePart?.divisionName ||
    state.global?.divisionName ||
    '';

  if (!divisionName) {
    const divLevel = activePart?.divisionLevel ?? state.global?.divisionLevel;
    if (divLevel !== undefined && divLevel !== null) {
      divisionName = `${divLevel}ª División`;
    }
  }

  const rawStatus = (state.status || state.state || '').toUpperCase();
  const isJoined = (rawStatus === 'JOINED' || (parts.length > 0 && !!rawClubId)) && rawStatus !== 'NOT_JOINED';

  if (!isJoined || !rawClubId) {
    return {
      status: 'NOT_JOINED',
      competitionId: rawCompetitionId,
    };
  }

  return {
    status: 'JOINED',
    competitionId: rawCompetitionId,
    seasonId: rawSeasonId || undefined,
    clubId: rawClubId,
    competitionUnitId: rawUnitId || undefined,
    groupId: rawUnitId || undefined,
    divisionName: divisionName || undefined,
    clubName: clubName || undefined,
    clubCode: clubCode || undefined,
  };
}

export const competitionApi = {
  /**
   * GET /api/competitions/seasons/{seasonId}/clubs/{clubId}/calendar
   * Por defecto selecciona la jornada actual; entre jornadas, la próxima.
   * Las flechas de navegación consultan con el query parameter ?matchdayNumber=X (ej. ?matchdayNumber=11).
   * Mock fallback: /mocks/competition/calendar.json
   */
  async getClubCalendar(
    seasonId: string,
    clubId: string,
    matchdayNumber?: number
  ): Promise<CompetitionMatchdayResponse> {
    const query = matchdayNumber !== undefined ? `?matchdayNumber=${matchdayNumber}` : '';
    return apiSimulator.callWithMockFallback<CompetitionMatchdayResponse>({
      method: 'GET',
      endpoint: clubPath(`/calendar${query}`, clubId),
      service: 'competition',
      mockName: 'calendar',
      transformMock: (mock: CompetitionMatchdayResponse) => {
        if (matchdayNumber !== undefined && mock) {
          const total = mock.totalMatchdays || 38;
          const current = mock.currentMatchdayNumber || 8;
          return {
            ...mock,
            matchdayNumber,
            localMatchdayNumber: matchdayNumber,
            previousMatchdayNumber: matchdayNumber > 1 ? matchdayNumber - 1 : null,
            nextMatchdayNumber: matchdayNumber < total ? matchdayNumber + 1 : null,
            status:
              matchdayNumber < current
                ? 'FINISHED'
                : matchdayNumber === current
                ? mock.status || 'PLANNED'
                : 'PLANNED',
          };
        }
        return mock;
      },
    });
  },

  /**
   * Resuelve el seasonId y clubId activos del usuario para consultas de competición y calendario
   * exclusivamente a partir de la respuesta autoritativa de GET /api/me/competitions.
   */
  async resolveActiveSeasonAndClubId(
    overrideSeasonId?: string | null,
    overrideClubId?: string | null
  ): Promise<{ seasonId: string; clubId: string }> {
    if (overrideSeasonId && overrideClubId) {
      return { seasonId: overrideSeasonId, clubId: overrideClubId };
    }

    const state = await this.getMyCompetitions();
    const ctx = extractActiveCompetitionContext(state);

    const seasonId = overrideSeasonId || ctx.seasonId;
    const clubId = overrideClubId || ctx.clubId;

    if (!seasonId || !clubId) {
      throw new Error(
        'No se pudo determinar el club y temporada activos del usuario desde el servidor.'
      );
    }

    return { seasonId, clubId };
  },

  /**
   * GET /api/me/competitions
   * Estado de las competiciones del usuario
   * Mock fallback: /mocks/competition/me-competitions.json
   */
  async getMyCompetitions(): Promise<UserCompetitionsState> {
    return apiSimulator.callWithMockFallback<UserCompetitionsState>({
      method: 'GET',
      endpoint: '/api/me/competitions',
      service: 'competition',
      mockName: 'me-competitions',
      allowMockFallback: false,
    });
  },

  /**
   * GET /api/groups/{competitionGroupId}/standings
   * Clasificación en vivo del grupo de competición.
   * Mock fallback: /mocks/competition/standings.json
   */
  async getGroupStandings(competitionGroupId: string): Promise<GroupStandingItem[]> {
    const rows = await apiSimulator.callWithMockFallback<GroupStandingItem[]>({
      method: 'GET',
      endpoint: clubPath('/standings',competitionGroupId),
      service: 'competition',
      mockName: 'standings',
    });
    cacheClubShields(rows);
    return rows;
  },

  /**
   * GET /api/competitions/{competitionId}/structure
   * Estructura de la competición activa: divisiones, ciclos, clubes por grupo
   * Mock fallback: /mocks/competition/competition-structure.json
   */
  async getCompetitionStructure(competitionId: string): Promise<CompetitionStructure> {
    return apiSimulator.callWithMockFallback<CompetitionStructure>({
      method: 'GET',
      endpoint: `/api/competitions/${encodeURIComponent(competitionId)}/structure`,
      service: 'competition',
      mockName: 'competition-structure',
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/matchdays/:number/simulate
   * Mock fallback: /mocks/competition/simulate-matchday.json
   */
  async simulateMatchday(
    leagueId: string,
    matchdayNumber: number,
    fixturesCount: number
  ): Promise<{
    success: boolean;
    matchdayNumber: number;
    fixturesResolved: number;
  }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${leagueId}/matchdays/${matchdayNumber}/simulate`,
      body: { matchdayNumber, fixturesCount },
      service: 'competition',
      mockName: 'simulate-matchday',
      transformMock: (mock) => ({
        ...mock,
        matchdayNumber,
        fixturesResolved: fixturesCount,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/matchdays/advance
   * Mock fallback: /mocks/competition/advance-matchday.json
   */
  async advanceMatchday(
    leagueId: string,
    nextMatchdayNumber: number
  ): Promise<{ currentMatchday: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${leagueId}/matchdays/advance`,
      body: { nextMatchdayNumber },
      service: 'competition',
      mockName: 'advance-matchday',
      transformMock: (mock) => ({
        ...mock,
        currentMatchday: nextMatchdayNumber,
      }),
    });
  },

  /**
   * POST /api/v1/leagues/:leagueId/editorial-ratings/publish
   * Mock fallback: /mocks/competition/publish-editorial-ratings.json
   */
  async publishEditorialRatings(
    leagueId: string,
    ratings: PlayerEditorialRating[]
  ): Promise<{ success: boolean; publishedCount: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: `/api/v1/leagues/${leagueId}/editorial-ratings/publish`,
      body: { ratingsCount: ratings.length },
      service: 'competition',
      mockName: 'publish-editorial-ratings',
      transformMock: (mock) => ({
        ...mock,
        publishedCount: ratings.length,
      }),
    });
  },

  /**
   * GET /api/matches/:fixtureId/live
   * Visualiza los jugadores y puntuaciones del partido.
   * Mock fallback: /mocks/competition/match-live.json
   */
  async getMatchLive(fixtureId: string): Promise<MatchLiveResponse> {
    return apiSimulator.callWithMockFallback<MatchLiveResponse>({
      method: 'GET',
      endpoint: clubPath(`/matches/${encodeURIComponent(fixtureId)}/live`),
      service: 'competition',
      mockName: 'match-live',
      transformMock: (mock: MatchLiveResponse) => ({
        ...mock,
        fixtureId,
      }),
    });
  },

  /**
   * GET /api/me/competitions/next-match
   * Datos específicos del próximo partido para el dashboard:
   * club local, visitante, estadio, jornada y patrocinadores de camiseta y estadio.
   * Mock fallback: /mocks/competition/next-match.json
   */
  async getNextMatch(): Promise<NextMatchResponse> {
    return apiSimulator.callWithMockFallback<NextMatchResponse>({
      method: 'GET',
      endpoint: clubPath('/next-match'),
      service: 'competition',
      mockName: 'next-match',
    });
  },

  /**
   * GET /api/clubs/{clubId}/matchdays/{matchdayId}/tactical-lineup
   * Consulta el once capturado de la jornada, suplentes en banquillo,
   * jugadores retirados y ventanas/sustituciones restantes.
   */
  async getTacticalLineup(
    clubId: string,
    matchdayId: string
  ): Promise<TacticalLineupResponse> {
    return apiSimulator.callWithMockFallback<TacticalLineupResponse>({
      method: 'GET',
      endpoint: clubPath(`/matchdays/${encodeURIComponent(matchdayId)}/tactical-lineup`, clubId),
      service: 'competition',
      allowMockFallback: false,
    });
  },

  /**
   * POST /api/clubs/{clubId}/matchdays/{matchdayId}/substitution-windows
   * Confirma expresamente un lote de 1 o más sustituciones.
   * Envía la cabecera obligatoria Idempotency-Key.
   */
  async confirmSubstitutionWindow(
    clubId: string,
    matchdayId: string,
    payload: ConfirmSubstitutionWindowPayload,
    idempotencyKey: string
  ): Promise<SubstitutionWindowResponse> {
    return apiSimulator.callWithMockFallback<SubstitutionWindowResponse>({
      method: 'POST',
      endpoint: clubPath(`/matchdays/${encodeURIComponent(matchdayId)}/substitution-windows`, clubId),
      body: payload,
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
      service: 'competition',
      allowMockFallback: false,
    });
  },

  /**
   * GET /api/clubs/{clubId}/matchdays/{matchdayId}/substitution-windows
   * Consulta el historial independiente de ventanas de sustitución del club.
   */
  async getSubstitutionWindows(
    clubId: string,
    matchdayId: string
  ): Promise<SubstitutionWindow[]> {
    return apiSimulator.callWithMockFallback<SubstitutionWindow[]>({
      method: 'GET',
      endpoint: clubPath(`/matchdays/${encodeURIComponent(matchdayId)}/substitution-windows`, clubId),
      service: 'competition',
      allowMockFallback: false,
    });
  },
};
